import type { Level } from './constants';
import { resultReading, type ResultReading } from './tags';
import { endsAfter, parseTimeValue, type TimeValue } from './time-value';

/**
 * The clipped border (docs/HEALTH_SIGNALS_V2.md §5, amended 2026-10-05 by
 * docs/DESIGN_SYSTEM.md "Values and entries" and by the stub's result, Leon
 * 2026-10-05; scenario HS-K5): what an item learns about a dependency its
 * viewer cannot see. A stub carries its result (open, done or cancelled,
 * `stubResult`), so the dependency reports every change of it, and beyond
 * that only when its end threatens the dependent; each report is a change
 * kind with zero content:
 *
 *   - `done`       - it was closed with a delivery standing;
 *   - `cancelled`  - it was closed without one;
 *   - `reopened`   - it is open again, so a dependent that read "done" does
 *                    not go on relying on it;
 *   - `ends_after` - an end-date move put its declared end after the
 *                    dependent's declared end.
 *
 * Nothing else crosses: a delivery or a withdrawn delivery while it is open,
 * an end-date move that does not cross the dependent's end, a vacated
 * requester seat, and every standing statement of the hidden dependency
 * (late, quiet, no requester, a declared signal, active or idle) report
 * nothing. Either side may sever the edge, which ends the border with it.
 *
 * The report's level is the kind's (`clippedChangeLevel`), never the hidden
 * event's: an event's level records how the act stood to its own item (a Close
 * is `warning` unless the closer was the only party, an end move is `warning`
 * when the mover does not hold the field's owning seat), and that is the far
 * side's seats and roles.
 *
 * This module is the one place the rule lives. Every producer that relays
 * something about an item its viewer cannot see asks it: the feed's clipped
 * lane (`gatherBorderCrossings`, src/read/access-inputs.ts), the neighbourhood
 * digest (src/read/digest.ts), the counts (`dependencyCountsOpen`), and,
 * through `dependencyEndsAfter`, the schedule-conflict fact
 * (src/domain/facts.ts) and the cost line at the date move
 * (`scheduleConflictCosts`, src/read/facts.ts). Pure: atoms and declared ends
 * come in, a change kind, a result or a boolean goes out, and no date leaves.
 */

/** What a dependency the viewer cannot see may report. */
export type ClippedChangeKind = 'done' | 'cancelled' | 'reopened' | 'ends_after';

/**
 * The level a clipped report carries, from its change kind alone. The hidden
 * event's own level is not used: it says whether the hidden item had other
 * parties (a Close) or whether the mover held the field's owning seat (an end
 * move), which the dependent's viewer may not learn. A cancellation threatens
 * the dependent outright (`warning`); a reopen, and an end past the
 * dependent's end, are threats to be watched (`notice`); a dependency done
 * threatens nothing (`info`).
 */
export function clippedChangeLevel(kind: ClippedChangeKind): Level {
  switch (kind) {
    case 'cancelled':
      return 'warning';
    case 'reopened':
    case 'ends_after':
      return 'notice';
    case 'done':
      return 'info';
  }
}

/**
 * What a viewer may know of an item they see only as its stub, beside its
 * name, requester and relationships (Leon, 2026-10-05): its result, exactly
 * one of open, done or cancelled, read from the closed and delivered atoms as
 * every surface reads them (`resultReading`). An item in review is open: a
 * delivery awaiting its review is behind the scenes, as are its
 * health, whether it is active or idle, its dates and its seats beyond the
 * requester.
 */
export function stubResult(item: { closed: boolean; delivered: boolean }): ResultReading {
  return resultReading(item);
}

/** What happened on, or holds of, the hidden dependency, as a producer met it. */
export type HiddenChange =
  /** A Close: with a delivery standing (done) or without one (cancelled). */
  | { kind: 'closed'; delivered: boolean }
  /** A Reopen. */
  | { kind: 'reopened' }
  /** A delivery made (`delivered`) or withdrawn (not), and whether the item
   *  was closed at the time: a closed item's result moves with it. */
  | { kind: 'delivery'; delivered: boolean; closed: boolean }
  /** The declared end was set, moved or cleared: the stored values before and
   *  after the change, as the event carries them (unparsed). */
  | { kind: 'end_moved'; previousEnd: unknown; nextEnd: unknown }
  /** The requester seat emptied. */
  | { kind: 'requester_vacated' }
  /** A standing statement of the hidden dependency: a fact or a declared
   *  signal, as the digest gathers it. */
  | { kind: 'statement' };

/** A stored declared end as a time value; null when undeclared or unparseable
 *  (an end nobody can read promises nothing, so it conflicts with nothing). */
export function declaredEnd(raw: unknown): TimeValue | null {
  if (typeof raw !== 'string' || raw.trim().length === 0) return null;
  const parsed = parseTimeValue(raw);
  return parsed.ok ? parsed.value : null;
}

/**
 * The ends-after comparison: does the dependency's declared end lie after the
 * dependent's declared end? The schedule conflict of docs/HEALTH_SIGNALS_V2.md
 * §9: both promises declared, and they cannot both hold. False when either end
 * is undeclared. Grain-honest through `endsAfter` (intervals compare by their
 * ends). Only the boolean leaves, so a caller on the side that cannot see one
 * of the two items learns nothing about its date.
 */
export function dependencyEndsAfter(
  dependencyEnd: TimeValue | null,
  dependentEnd: TimeValue | null,
): boolean {
  return dependencyEnd !== null && dependentEnd !== null && endsAfter(dependencyEnd, dependentEnd);
}

/**
 * The rule: what a change on a hidden dependency reports to the dependent, or
 * null for nothing. A change of its result (a Close, a Reopen, a delivery
 * made or withdrawn while closed) reports the result it left, a Reopen as
 * `reopened`; a delivery made or withdrawn while open changes no result and
 * reports nothing. `dependentEnd` is the dependent's declared end as it stood
 * when the change happened (`dependentEndAt`), so a later move of the
 * dependent's own end neither creates a past report nor re-reads old dates.
 *
 * An end move reports `ends_after` only when it CROSSES the dependent's end:
 * the new end lies after it and the previous one did not. A move that stays
 * before the dependent's end, or one that was already after it and moves
 * further, says nothing new about the threat (the dependent's own
 * schedule-conflict fact already stands for the second).
 */
export function clippedChangeKind(
  change: HiddenChange,
  dependentEnd: TimeValue | null,
): ClippedChangeKind | null {
  switch (change.kind) {
    case 'closed':
      return closedKind(change.delivered);
    case 'reopened':
      return 'reopened';
    case 'delivery':
      return change.closed ? closedKind(change.delivered) : null;
    case 'end_moved': {
      const wasAfter = dependencyEndsAfter(declaredEnd(change.previousEnd), dependentEnd);
      const isAfter = dependencyEndsAfter(declaredEnd(change.nextEnd), dependentEnd);
      return isAfter && !wasAfter ? 'ends_after' : null;
    }
    case 'requester_vacated':
    case 'statement':
      return null;
  }
}

/** The kind a closed dependency reports: the result it reads as. */
function closedKind(delivered: boolean): ClippedChangeKind {
  return stubResult({ closed: true, delivered }) === 'done' ? 'done' : 'cancelled';
}

/** One change of the dependent's declared end in the log: its position, and
 *  the stored end it replaced. */
export interface EndChange {
  seq: number;
  endBefore: unknown;
}

/**
 * The dependent's declared end as it stood at log position `seq`: the end that
 * the first later change replaced, or the current end when nothing changed it
 * since. `laterChanges` are the dependent's end changes in any order; only
 * those after `seq` count.
 */
export function dependentEndAt(
  seq: number,
  currentEnd: unknown,
  laterChanges: readonly EndChange[],
): TimeValue | null {
  let first: EndChange | null = null;
  for (const change of laterChanges) {
    if (change.seq > seq && (first === null || change.seq < first.seq)) first = change;
  }
  return declaredEnd(first === null ? currentEnd : first.endBefore);
}

/**
 * Where the current schedule conflict over one dependency edge began
 * (docs/HEALTH_SIGNALS_V2.md §9, HS-G3): the log position of the latest end
 * change, of either item, before which the dependency did not end after the
 * dependent. `dependencyEnd` and `dependentEnd` are the stored ends now; each
 * change list holds that item's end changes (any order). Null when the
 * dependency does not end after the dependent now; 0 when it has done so
 * through every recorded change (both ends set at birth).
 *
 * For a dependency move this is the crossing `clippedChangeKind` reports as
 * `ends_after`; for a move of the dependent's own end, a change its viewers
 * see. So the position says nothing about the dependency that the dependent's
 * viewers are not told. It identifies the episode on the server only.
 */
export function conflictEpisodeStart(
  dependencyEnd: unknown,
  dependentEnd: unknown,
  dependencyChanges: readonly EndChange[],
  dependentChanges: readonly EndChange[],
): number | null {
  let dep = dependencyEnd;
  let own = dependentEnd;
  if (!dependencyEndsAfter(declaredEnd(dep), declaredEnd(own))) return null;
  const changes = [
    ...dependencyChanges.map((c) => ({ ...c, side: 'dependency' as const })),
    ...dependentChanges.map((c) => ({ ...c, side: 'dependent' as const })),
  ].sort((a, b) => b.seq - a.seq);
  for (const change of changes) {
    if (change.side === 'dependency') dep = change.endBefore;
    else own = change.endBefore;
    if (!dependencyEndsAfter(declaredEnd(dep), declaredEnd(own))) return change.seq;
  }
  return 0;
}

/**
 * Whether a dependency counts as open in a count shown to a viewer
 * (2026-10-05): by its result, open counts and done or cancelled do not. One
 * the viewer can open and one they see only as its stub count the same way,
 * since the stub carries its result (`stubResult`); a count therefore says
 * nothing of a hidden dependency beyond what its stub row says. Every count
 * of open dependencies, parents and dependents asks this function.
 */
export function dependencyCountsOpen(dep: { closed: boolean; delivered: boolean }): boolean {
  return stubResult(dep) === 'open';
}
